import React, { useMemo } from "react";
import {
  useGetEnglishQuizBlueprintByQuizQuery,
  useGetEnglishQuizBlueprintByIdQuery,
} from "../../slices/englishQuizBlueprintApiSlice";

import BlueprintSectionList from "./components/BlueprintSectionList";
import BlueprintTotals from "./components/BlueprintTotals";
import BlueprintValidationSummary from "./components/BlueprintValidationSummary";
import BlueprintValidationResult from "./components/BlueprintValidationResult";

function getErrorMessage(error) {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return (
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    "Unable to load the blueprint."
  );
}

function getBlueprint(response) {
  if (!response) {
    return null;
  }

  return (
    response?.data?.blueprint ||
    response?.data ||
    response?.blueprint ||
    response
  );
}

function normalizeSection(section, index) {
  return {
    ...section,
    id:
      section?.id ||
      section?._id ||
      `section-${index}`,
    order: Number(
      section?.order ?? index + 1
    ),
    selectionCount: Number(
      section?.selectionCount ??
        section?.count ??
        0
    ),
    marksPerQuestion: Number(
      section?.marksPerQuestion ??
        section?.marks ??
        0
    ),
    negativeMarks: Number(
      section?.negativeMarks ?? 0
    ),
  };
}

function getSections(blueprint) {
  if (Array.isArray(blueprint?.sections)) {
    return blueprint.sections;
  }

  if (
    Array.isArray(
      blueprint?.sectionSettings
    )
  ) {
    return blueprint.sectionSettings;
  }

  return [];
}

function getStatusClasses(status) {
  const normalized = String(
    status || "draft"
  ).toLowerCase();

  if (normalized === "published") {
    return "bg-emerald-50 text-emerald-700 ring-emerald-600/20";
  }

  if (normalized === "active") {
    return "bg-blue-50 text-blue-700 ring-blue-600/20";
  }

  if (normalized === "archived") {
    return "bg-gray-100 text-gray-600 ring-gray-500/20";
  }

  if (normalized === "inactive") {
    return "bg-amber-50 text-amber-700 ring-amber-600/20";
  }

  return "bg-gray-50 text-gray-600 ring-gray-500/20";
}

function formatDate(value) {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    undefined,
    {
      dateStyle: "medium",
      timeStyle: "short",
    }
  ).format(date);
}

function formatValue(value) {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return "—";
  }

  if (
    typeof value === "boolean"
  ) {
    return value ? "Yes" : "No";
  }

  return String(value);
}

function InfoItem({
  label,
  value,
  mono = false,
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </dt>

      <dd
        className={`mt-1 truncate text-sm font-medium text-gray-800 ${
          mono ? "font-mono text-xs" : ""
        }`}
      >
        {formatValue(value)}
      </dd>
    </div>
  );
}

function StatCard({
  label,
  value,
  description,
  color = "indigo",
}) {
  const colors = {
    indigo:
      "border-indigo-100 bg-indigo-50 text-indigo-700",
    emerald:
      "border-emerald-100 bg-emerald-50 text-emerald-700",
    amber:
      "border-amber-100 bg-amber-50 text-amber-700",
    blue:
      "border-blue-100 bg-blue-50 text-blue-700",
  };

  return (
    <div
      className={`rounded-xl border p-4 ${
        colors[color] ||
        colors.indigo
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
        {label}
      </p>

      <p className="mt-1 text-2xl font-bold">
        {value}
      </p>

      {description && (
        <p className="mt-1 text-[10px] opacity-70">
          {description}
        </p>
      )}
    </div>
  );
}

function SettingBadge({
  label,
  value,
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
      <p className="text-[9px] font-semibold uppercase tracking-wide text-gray-400">
        {label}
      </p>

      <p className="mt-0.5 text-xs font-medium text-gray-700">
        {formatValue(value)}
      </p>
    </div>
  );
}

function SectionOverview({
  section,
  index,
}) {
  const marks =
    Number(
      section.selectionCount || 0
    ) *
    Number(
      section.marksPerQuestion || 0
    );

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="flex flex-col gap-4 border-b border-gray-100 p-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-xs font-bold text-indigo-600">
            {index + 1}
          </div>

          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900">
              {section.name ||
                `Section ${index + 1}`}
            </h3>

            {section.description && (
              <p className="mt-1 text-xs leading-5 text-gray-500">
                {section.description}
              </p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold text-gray-600">
            {section.selectionCount || 0}{" "}
            questions
          </span>

          <span className="rounded-full bg-indigo-50 px-2.5 py-1 text-[10px] font-semibold text-indigo-600">
            {marks} marks
          </span>
        </div>
      </div>

      <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <SettingBadge
          label="Selection Type"
          value={
            section.selectionType
          }
        />

        <SettingBadge
          label="Question Type"
          value={
            section.questionType
          }
        />

        <SettingBadge
          label="Group Type"
          value={section.groupType}
        />

        <SettingBadge
          label="Difficulty"
          value={section.difficulty}
        />

        <SettingBadge
          label="Chapter"
          value={
            section.chapterName ||
            section.chapterId
          }
        />

        <SettingBadge
          label="Selection Count"
          value={
            section.selectionCount
          }
        />

        <SettingBadge
          label="Marks / Question"
          value={
            section.marksPerQuestion
          }
        />

        <SettingBadge
          label="Negative Marks"
          value={
            section.negativeMarks
          }
        />

        <SettingBadge
          label="Random Questions"
          value={
            section.randomizeQuestions
          }
        />

        <SettingBadge
          label="Random Options"
          value={
            section.randomizeOptions
          }
        />

        <SettingBadge
          label="Random Sections"
          value={
            section.randomizeSections
          }
        />

        <SettingBadge
          label="Order"
          value={section.order}
        />
      </div>
    </div>
  );
}

export default function EnglishQuizBlueprintDetails({
  quizId,
  blueprintId,
  blueprint: initialBlueprint = null,
  validationResult = null,
  onEdit,
  onBack,
  onRefresh,
  className = "",
}) {
  const shouldFetchById =
    Boolean(blueprintId) &&
    !initialBlueprint;

  const shouldFetchByQuiz =
    Boolean(quizId) &&
    !initialBlueprint &&
    !blueprintId;

  const {
    data: blueprintById,
    isLoading: loadingById,
    isFetching: fetchingById,
    error: errorById,
    refetch: refetchById,
  } = useGetEnglishQuizBlueprintByIdQuery(
    blueprintId,
    {
      skip: !shouldFetchById,
    }
  );

  const {
    data: blueprintByQuiz,
    isLoading: loadingByQuiz,
    isFetching: fetchingByQuiz,
    error: errorByQuiz,
    refetch: refetchByQuiz,
  } = useGetEnglishQuizBlueprintByQuizQuery(
    quizId,
    {
      skip: !shouldFetchByQuiz,
    }
  );

  const blueprint = useMemo(
    () =>
      initialBlueprint ||
      getBlueprint(
        blueprintById
      ) ||
      getBlueprint(
        blueprintByQuiz
      ),
    [
      initialBlueprint,
      blueprintById,
      blueprintByQuiz,
    ]
  );

  const sections = useMemo(
    () =>
      getSections(blueprint)
        .map(normalizeSection)
        .sort(
          (a, b) =>
            Number(a.order) -
            Number(b.order)
        ),
    [blueprint]
  );

  const totalQuestions = useMemo(
    () =>
      sections.reduce(
        (total, section) =>
          total +
          Number(
            section.selectionCount ||
              0
          ),
        0
      ),
    [sections]
  );

  const totalMarks = useMemo(
    () =>
      sections.reduce(
        (total, section) =>
          total +
          Number(
            section.selectionCount ||
              0
          ) *
            Number(
              section.marksPerQuestion ||
                0
            ),
        0
      ),
    [sections]
  );

  const totalNegativeMarks =
    useMemo(
      () =>
        sections.reduce(
          (total, section) =>
            total +
            Number(
              section.selectionCount ||
                0
            ) *
              Number(
                section.negativeMarks ||
                  0
              ),
          0
        ),
      [sections]
    );

  const isLoading =
    loadingById ||
    loadingByQuiz ||
    fetchingById ||
    fetchingByQuiz;

  const error =
    errorById || errorByQuiz;

  const handleRefresh =
    async () => {
      if (onRefresh) {
        await onRefresh();
        return;
      }

      if (shouldFetchById) {
        await refetchById();
        return;
      }

      if (shouldFetchByQuiz) {
        await refetchByQuiz();
      }
    };

  if (isLoading) {
    return (
      <div
        className={`w-full space-y-5 ${className}`}
      >
        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="h-5 w-64 animate-pulse rounded bg-gray-200" />
          <div className="mt-2 h-3 w-96 max-w-full animate-pulse rounded bg-gray-100" />

          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[1, 2, 3].map(
              (item) => (
                <div
                  key={item}
                  className="h-24 animate-pulse rounded-xl bg-gray-100"
                />
              )
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-gray-200 bg-white p-6">
          <div className="h-4 w-40 animate-pulse rounded bg-gray-200" />

          <div className="mt-5 space-y-3">
            {[1, 2, 3].map(
              (item) => (
                <div
                  key={item}
                  className="h-32 animate-pulse rounded-xl bg-gray-100"
                />
              )
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error && !blueprint) {
    return (
      <div
        className={`rounded-xl border border-red-200 bg-red-50 p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-900">
              Unable to load blueprint
            </h3>

            <p className="mt-1 text-xs leading-5 text-red-700">
              {getErrorMessage(error)}
            </p>

            <button
              type="button"
              onClick={handleRefresh}
              className="mt-3 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!blueprint) {
    return (
      <div
        className={`rounded-xl border border-amber-200 bg-amber-50 p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-amber-900">
              Blueprint not found
            </h3>

            <p className="mt-1 text-xs leading-5 text-amber-700">
              No English quiz blueprint is
              available for the requested quiz.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const status =
    blueprint.status ||
    blueprint.state ||
    "draft";

  const resolvedBlueprintId =
    blueprint.id ||
    blueprint._id ||
    blueprint.blueprintId ||
    blueprintId;

  return (
    <div
      className={`w-full space-y-5 ${className}`}
    >
      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="mr-1 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 transition hover:bg-gray-50 hover:text-gray-700"
                    aria-label="Back"
                  >
                    <svg
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="h-4 w-4"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.79 15.79a.75.75 0 01-1.06 0l-5-5a.75.75 0 010-1.06l5-5a.75.75 0 111.06 1.06L8.32 10.26l4.47 4.47a.75.75 0 010 1.06z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}

                <h1 className="truncate text-lg font-semibold text-gray-900">
                  {blueprint.name ||
                    blueprint.title ||
                    "English Quiz Blueprint"}
                </h1>

                <span
                  className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${getStatusClasses(
                    status
                  )}`}
                >
                  {String(status).replace(
                    /_/g,
                    " "
                  )}
                </span>
              </div>

              {blueprint.description && (
                <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-500">
                  {blueprint.description}
                </p>
              )}
            </div>

            <div className="flex shrink-0 items-center gap-2">
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="h-4 w-4"
                    aria-hidden="true"
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-8.5 8.5-3.5.5.5-3.5 8.672-8.328z" />
                    <path
                      fillRule="evenodd"
                      d="M4 16.5h12a.5.5 0 010 1H4a.5.5 0 010-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Edit Blueprint
                </button>
              )}

              <button
                type="button"
                onClick={handleRefresh}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 9.5a6.5 6.5 0 10-1.9 4.6"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 5.5v4h-4"
                  />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6 p-5 sm:p-6">
          <dl className="grid gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-4">
            <InfoItem
              label="Blueprint ID"
              value={
                resolvedBlueprintId
              }
              mono
            />

            <InfoItem
              label="Quiz ID"
              value={
                blueprint.quizId ||
                quizId
              }
              mono
            />

            <InfoItem
              label="Created"
              value={formatDate(
                blueprint.createdAt
              )}
            />

            <InfoItem
              label="Last Updated"
              value={formatDate(
                blueprint.updatedAt
              )}
            />

            <InfoItem
              label="Published At"
              value={formatDate(
                blueprint.publishedAt
              )}
            />

            <InfoItem
              label="Created By"
              value={
                blueprint.createdBy?.name ||
                blueprint.createdBy?.email ||
                blueprint.createdBy
              }
            />

            <InfoItem
              label="Updated By"
              value={
                blueprint.updatedBy?.name ||
                blueprint.updatedBy?.email ||
                blueprint.updatedBy
              }
            />

            <InfoItem
              label="Version"
              value={
                blueprint.version
              }
            />
          </dl>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Sections"
              value={sections.length}
              description="Configured blueprint sections"
              color="indigo"
            />

            <StatCard
              label="Questions"
              value={totalQuestions}
              description="Questions selected by blueprint"
              color="blue"
            />

            <StatCard
              label="Total Marks"
              value={totalMarks}
              description="Maximum marks"
              color="emerald"
            />

            <StatCard
              label="Negative Marks"
              value={totalNegativeMarks}
              description="Potential negative marks"
              color="amber"
            />
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Section Overview
            </h2>

            <p className="mt-1 text-xs text-gray-500">
              Read-only view of the question selection
              configuration.
            </p>
          </div>
        </div>

        <div className="space-y-3 p-5 sm:p-6">
          {sections.length === 0 ? (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-10 text-center">
              <p className="text-sm font-medium text-gray-700">
                No sections configured
              </p>

              <p className="mt-1 text-xs text-gray-500">
                This blueprint does not contain any
                selection sections yet.
              </p>
            </div>
          ) : (
            sections.map(
              (section, index) => (
                <SectionOverview
                  key={
                    section.id ||
                    index
                  }
                  section={section}
                  index={index}
                />
              )
            )
          )}
        </div>
      </div>

      {sections.length > 0 && (
        <div className="rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
            <h2 className="text-sm font-semibold text-gray-900">
              Blueprint Summary
            </h2>

            <p className="mt-1 text-xs text-gray-500">
              Aggregate totals calculated from all
              configured sections.
            </p>
          </div>

          <div className="p-5 sm:p-6">
            <BlueprintTotals
              sections={sections}
              totalQuestions={
                totalQuestions
              }
              totalMarks={totalMarks}
            />
          </div>
        </div>
      )}

      {validationResult && (
        <div className="space-y-4">
          <BlueprintValidationSummary
            result={validationResult}
          />

          <BlueprintValidationResult
            result={validationResult}
          />
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-5 py-4 sm:px-6">
          <h2 className="text-sm font-semibold text-gray-900">
            Configuration Snapshot
          </h2>

          <p className="mt-1 text-xs text-gray-500">
            Additional blueprint-level settings.
          </p>
        </div>

        <div className="grid gap-3 p-5 sm:grid-cols-2 lg:grid-cols-4 sm:p-6">
          <SettingBadge
            label="Randomize Sections"
            value={
              blueprint.randomizeSections
            }
          />

          <SettingBadge
            label="Randomize Questions"
            value={
              blueprint.randomizeQuestions
            }
          />

          <SettingBadge
            label="Randomize Options"
            value={
              blueprint.randomizeOptions
            }
          />

          <SettingBadge
            label="Active"
            value={
              blueprint.isActive ??
              blueprint.active
            }
          />

          <SettingBadge
            label="Selection Mode"
            value={
              blueprint.selectionType
            }
          />

          <SettingBadge
            label="Total Questions"
            value={
              blueprint.totalQuestions ??
              totalQuestions
            }
          />

          <SettingBadge
            label="Total Marks"
            value={
              blueprint.totalMarks ??
              totalMarks
            }
          />

          <SettingBadge
            label="Passing Marks"
            value={
              blueprint.passingMarks
            }
          />
        </div>
      </div>
    </div>
  );
}